import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';

import type {
  AttendanceRule,
  EscalationRule,
  ManualPointCategory,
  MissedShiftRule,
  ThresholdDraftRule,
} from '../settings/types';
import { sortThresholdRulesByPoints } from '../thresholdStatus';
import { checkForOverlappingAttendanceRules } from './checkForOverlappingAttendanceRules';
import { mockRulesData } from './mockRulesData';

export const POINTS_RULE_TYPES = {
  ATTENDANCE_BASED_RULES: 'attendanceBasedRules',
  THRESHOLD_AND_ESCALATIONS: 'thresholdAndEscalations',
};

export const POINTS_RULE_SUBTYPES = {
  LATE_ARRIVALS: 'lateArrivals',
  EARLY_DEPARTURES: 'earlyDepartures',
  MISSED_SHIFTS: 'missedShifts',
  ESCALATION_RULES: 'escalationRules',
};

export const POINTS_PRECISION = 2;

export type PointsPolicyRules = {
  attendanceBasedRules: {
    lateArrivals: AttendanceRule[];
    earlyDepartures: AttendanceRule[];
    missedShifts: MissedShiftRule[];
  };
  thresholdAndEscalations: {
    escalationRules: ThresholdDraftRule[];
  };
};

const emptyPolicyRules = (): PointsPolicyRules => ({
  attendanceBasedRules: { lateArrivals: [], earlyDepartures: [], missedShifts: [] },
  thresholdAndEscalations: { escalationRules: [] },
});

export interface PointsSettingsState {
  id?: number;
  policyName?: string;
  // Points expiration
  rollingExpiryDays: number;
  rules: PointsPolicyRules;
  isEnableManagerApproval: boolean;
  // UI-only: off still keeps draft rows until Save, which then sends []
  isThresholdsEnabled: boolean;
  originalData: {
    rollingExpiryDays: number;
    rules: PointsPolicyRules;
    isThresholdsEnabled: boolean;
    manualSettings: ManualSettings;
  };
  isEnableRequiredDataValidation: boolean;
  manualSettings: ManualSettings;
}

interface ManualSettings {
  categories: ManualPointCategory[];
}
interface RootState {
  pointsSettings: PointsSettingsState;
}

const normalizePolicyRules = (rules): PointsPolicyRules => ({
  attendanceBasedRules: {
    lateArrivals: rules?.attendanceBasedRules?.lateArrivals ?? [],
    earlyDepartures: rules?.attendanceBasedRules?.earlyDepartures ?? [],
    missedShifts: rules?.attendanceBasedRules?.missedShifts ?? [],
  },
  thresholdAndEscalations: {
    escalationRules: sortThresholdRulesByPoints(rules?.thresholdAndEscalations?.escalationRules ?? []),
  },
});

const checkIsMissingRequiredData = ({ rule, ruleSubType }) => {
  switch (ruleSubType) {
    case POINTS_RULE_SUBTYPES.LATE_ARRIVALS:
    case POINTS_RULE_SUBTYPES.EARLY_DEPARTURES: {
      const { thresholdMaxMinutes, thresholdMinMinutes, appliesToDays, pointsAwarded } = rule;

      // "Above" range case, if it's not null, then it's an applicable field
      // so we check for undefined (meaning empty)
      // just early return failure case.
      if (thresholdMaxMinutes !== null) {
        if (thresholdMaxMinutes === undefined) {
          return true;
        }
      }
      return !(thresholdMinMinutes && appliesToDays && pointsAwarded);
    }

    case POINTS_RULE_SUBTYPES.MISSED_SHIFTS: {
      const { infractionType, pointsAwarded } = rule;
      return !(infractionType && pointsAwarded);
    }

    case POINTS_RULE_SUBTYPES.ESCALATION_RULES: {
      const { stakeholdersToNotify, escalationType, reviewCycle, disciplinaryNoteType, severity, pointsThreshold } =
        rule;
      if (escalationType === 'NO_ACTION') {
        return !(stakeholdersToNotify.length >= 2 && escalationType && pointsThreshold);
      }
      if (escalationType === 'DISCIPLINARY_NOTE') {
        return !(stakeholdersToNotify.length && escalationType && disciplinaryNoteType && severity && pointsThreshold);
      }
      if (escalationType === 'PIP_REVIEW') {
        return !(stakeholdersToNotify.length && escalationType && reviewCycle && pointsThreshold);
      }
      return false;
    }

    default:
      return false;
  }
};

const checkForMissingRequiredData = ({ ruleSubType, rules }) => {
  // logic to check for missing required data based on ruleSubType and rules

  // reset all isMissingRequiredData flags
  const rulesNew = rules.map((rule) => ({ ...rule, isMissingRequiredData: false }));

  for (let i = 0; i < rulesNew.length; i++) {
    if (checkIsMissingRequiredData({ rule: rulesNew[i], ruleSubType })) {
      rulesNew[i].isMissingRequiredData = true;
    }
  }
  return rulesNew;
};

/**
 * Checks if two rules are overlapping(including exact duplicates) based on rule subtype.
 *
 * For LATE_ARRIVALS and EARLY_DEPARTURES:
 * - Uses intelligent overlap detection across three dimensions:
 *   1. Days must overlap (appliesToDays)
 *   2. Time ranges must overlap (thresholdMinMinutes to thresholdMaxMinutes)
 *   3. Shift filters must overlap (shiftFilterMinHours to shiftFilterMaxHours)
 * - Detects both exact duplicates and partial overlaps
 * - Handles null values for "Above" mode and "any shift" scenarios
 *
 * For MISSED_SHIFTS:
 * - Simple equality check on infractionType
 *
 * For ESCALATION_RULES:
 * - Checks equality across multiple fields
 */
const checkOverlapInRules = ({ rule1, rule2, ruleSubType }) => {
  switch (ruleSubType) {
    case POINTS_RULE_SUBTYPES.LATE_ARRIVALS:
    case POINTS_RULE_SUBTYPES.EARLY_DEPARTURES: {
      // Use intelligent overlap detection for attendance rules
      return checkForOverlappingAttendanceRules(rule1, rule2);
    }

    case POINTS_RULE_SUBTYPES.MISSED_SHIFTS: {
      return rule1.infractionType === rule2.infractionType;
    }

    case POINTS_RULE_SUBTYPES.ESCALATION_RULES: {
      return (
        rule1.stakeholdersToNotify.length === rule2.stakeholdersToNotify.length &&
        rule1.escalationType === rule2.escalationType &&
        rule1.reviewCycle === rule2.reviewCycle &&
        rule1.disciplinaryNoteType === rule2.disciplinaryNoteType &&
        rule1.severity === rule2.severity
      );
    }

    default: {
      return false;
    }
  }
};

const checkForOverlappingRules = ({ ruleSubType, rules }) => {
  // logic to check for overlapping rules based on ruleSubType and rules

  // reset all isOverlapping flags
  const rulesNew = rules.map((rule) => ({ ...rule, isOverlapping: false }));

  for (let i = 0; i < rulesNew.length; i++) {
    for (let j = i + 1; j < rulesNew.length; j++) {
      if (checkOverlapInRules({ rule1: rulesNew[i], rule2: rulesNew[j], ruleSubType })) {
        rulesNew[i].isOverlapping = true;
        rulesNew[j].isOverlapping = true;
      }
    }
  }
  return rulesNew;
};

const initialState: PointsSettingsState = {
  rollingExpiryDays: null,
  rules: emptyPolicyRules(),
  isEnableManagerApproval: true,
  isThresholdsEnabled: false,
  // this is used to store the original data fetched from API for comparison
  // the comparison is used to show unsaved changes prompt
  originalData: {
    rollingExpiryDays: null,
    rules: emptyPolicyRules(),
    isThresholdsEnabled: false,
    manualSettings: { categories: [] },
  },
  manualSettings: {
    categories: [],
  },
  isEnableRequiredDataValidation: false,
};

const pointsSettingsSlice = createSlice({
  name: 'pointsSettings',
  initialState,
  reducers: {
    // Points expiration actions
    setRollingExpirationPeriodDays: (state, action: PayloadAction<number>) => {
      state.rollingExpiryDays = action.payload;
    },

    // used in case of mock demo data
    setPointsMockDemoState: (state) => {
      const rules = normalizePolicyRules(mockRulesData.rules);
      const isThresholdsEnabled = rules.thresholdAndEscalations.escalationRules.length > 0;
      return {
        ...state,
        ...mockRulesData,
        rules,
        isThresholdsEnabled,
        originalData: {
          rollingExpiryDays: mockRulesData.rollingExpiryDays,
          rules,
          isThresholdsEnabled,
          manualSettings: mockRulesData.manualSettings,
        },
      };
    },

    // used in case of api fetched data
    setPointsInitialData: (state, action) => {
      // a policy with no manual settings has to clear the live categories too, not
      // only the baseline - otherwise the previous policy's categories stay on screen
      // and can be saved onto this one
      const manualSettings = action.payload.manualSettings ?? { categories: [] };
      const rules = normalizePolicyRules(action.payload.rules);
      const isThresholdsEnabled = rules.thresholdAndEscalations.escalationRules.length > 0;

      return {
        ...state,
        ...action.payload,
        rules,
        manualSettings,
        isThresholdsEnabled,
        originalData: {
          rollingExpiryDays: action.payload.rollingExpiryDays,
          rules,
          isThresholdsEnabled,
          manualSettings,
        },
      };
    },

    setIsThresholdsEnabled: (state, action: PayloadAction<boolean>) => {
      state.isThresholdsEnabled = action.payload;
    },

    addThresholdRule: (state, action: PayloadAction<ThresholdDraftRule>) => {
      state.rules.thresholdAndEscalations.escalationRules.push(action.payload);
    },

    updateThresholdRule: (state, action: PayloadAction<{ index: number; patch: Partial<ThresholdDraftRule> }>) => {
      const { index, patch } = action.payload;
      const current = state.rules.thresholdAndEscalations.escalationRules[index];
      if (!current) {
        return;
      }
      state.rules.thresholdAndEscalations.escalationRules[index] = { ...current, ...patch };
    },

    removeThresholdRule: (state, action: PayloadAction<ThresholdDraftRule['id']>) => {
      const nextRules = state.rules.thresholdAndEscalations.escalationRules.filter(
        (rule) => rule.id !== action.payload
      );
      state.rules.thresholdAndEscalations.escalationRules = nextRules;
      if (nextRules.length === 0) {
        state.isThresholdsEnabled = false;
      }
    },

    setIsEnableRequiredDataValidation: (state) => {
      state.isEnableRequiredDataValidation = true;
    },

    addPointsRule: (
      state,
      action: PayloadAction<{
        ruleType: keyof typeof POINTS_RULE_TYPES;
        ruleSubType: keyof typeof POINTS_RULE_SUBTYPES;
        rule: MissedShiftRule | AttendanceRule | EscalationRule;
      }>
    ) => {
      const { ruleType, ruleSubType, rule } = action.payload;
      const newRulesList = [...state.rules[ruleType][ruleSubType], rule];
      const newRulesWithOverlapCheck = checkForOverlappingRules({ ruleSubType, rules: newRulesList });
      const newRulesWithMissingDataCheck = checkForMissingRequiredData({
        ruleSubType,
        rules: newRulesWithOverlapCheck,
      });
      state.rules[ruleType][ruleSubType] = newRulesWithMissingDataCheck;
    },

    updatePointsRule: (
      state,
      action: PayloadAction<{
        ruleType: keyof typeof POINTS_RULE_TYPES;
        ruleSubType: keyof typeof POINTS_RULE_SUBTYPES;
        rule: MissedShiftRule | AttendanceRule | EscalationRule;
        ruleIndex: number;
      }>
    ) => {
      const { ruleType, ruleSubType, rule, ruleIndex } = action.payload;
      const rulesArray = state.rules[ruleType][ruleSubType];
      const newRuleData = { ...rulesArray[ruleIndex], ...rule };
      const newRulesList = [...rulesArray];

      newRulesList[ruleIndex] = newRuleData;
      const newRulesWithOverlapCheck = checkForOverlappingRules({ ruleSubType, rules: newRulesList });
      const newRulesWithMissingDataCheck = checkForMissingRequiredData({
        ruleSubType,
        rules: newRulesWithOverlapCheck,
      });
      state.rules[ruleType][ruleSubType] = newRulesWithMissingDataCheck;
    },

    removePointsRule: (
      state,
      action: PayloadAction<{
        ruleType: keyof typeof POINTS_RULE_TYPES;
        ruleSubType: keyof typeof POINTS_RULE_SUBTYPES;
        ruleId: string | number;
      }>
    ) => {
      const { ruleType, ruleSubType, ruleId } = action.payload;

      let newRulesList = state.rules[ruleType][ruleSubType].filter((rule) => rule.id !== ruleId);
      // update displayOrder of remaining rules
      newRulesList = newRulesList.map((rule, index) => ({
        ...rule,
        displayOrder: index + 1,
      }));

      const newRulesWithOverlapCheck = checkForOverlappingRules({ ruleSubType, rules: newRulesList });
      const newRulesWithMissingDataCheck = checkForMissingRequiredData({
        ruleSubType,
        rules: newRulesWithOverlapCheck,
      });
      state.rules[ruleType][ruleSubType] = newRulesWithMissingDataCheck;
    },

    updateIsEnableManagerApproval: (state, action: PayloadAction<boolean>) => {
      state.isEnableManagerApproval = action.payload;
    },

    addManualPointsCategory: (state, action: PayloadAction<string>) => {
      state.manualSettings.categories.push({
        categoryType: action.payload,
        predefinedPointsEnabled: false,
        predefinedPoints: null,
        allowOverride: false,
      });
    },

    updateManualPointsCategory: (
      state,
      action: PayloadAction<{ index: number; patch: Partial<ManualPointCategory> }>
    ) => {
      const { index, patch } = action.payload;
      const category = state.manualSettings.categories[index];

      if (!category) {
        return;
      }

      const updatedCategory = { ...category, ...patch };

      // mirror the backend normalisation: the points value and the override only
      // exist while predefined points are enabled. Without this the store could hold
      // values that the server silently discards on save.
      if (!updatedCategory.predefinedPointsEnabled) {
        updatedCategory.predefinedPoints = null;
        updatedCategory.allowOverride = false;
      }

      state.manualSettings.categories[index] = updatedCategory;
    },

    removeManualPointsCategory: (state, action: PayloadAction<number>) => {
      state.manualSettings.categories.splice(action.payload, 1);
    },

    resetPointsSettingsState: () => initialState,
  },
});

export const {
  setRollingExpirationPeriodDays,
  addPointsRule,
  updatePointsRule,
  removePointsRule,
  updateIsEnableManagerApproval,
  setPointsMockDemoState,
  setPointsInitialData,
  setIsThresholdsEnabled,
  addThresholdRule,
  updateThresholdRule,
  removeThresholdRule,
  resetPointsSettingsState,
  setIsEnableRequiredDataValidation,
  addManualPointsCategory,
  updateManualPointsCategory,
  removeManualPointsCategory,
} = pointsSettingsSlice.actions;

// Selectors

export const useIsEnableManagerApproval = () =>
  useSelector((state: RootState) => state.pointsSettings.isEnableManagerApproval);

export const useIsThresholdsEnabled = () => useSelector((state: RootState) => state.pointsSettings.isThresholdsEnabled);

export const useOriginalPointsSettingsData = () => useSelector((state: RootState) => state.pointsSettings.originalData);

export const useRollingExpirationPeriodDays = () =>
  useSelector((state: RootState) => state.pointsSettings.rollingExpiryDays);

export const useManualPointsCategories = () =>
  useSelector((state: RootState) => state.pointsSettings.manualSettings.categories);

export const useIsEnableRequiredDataValidation = () =>
  useSelector((state: RootState) => state.pointsSettings.isEnableRequiredDataValidation);

export const useViolationPointsRules = () => useSelector((state: RootState) => state.pointsSettings.rules);

export const useThresholdDraftRules = () =>
  useSelector((state: RootState) => state.pointsSettings.rules.thresholdAndEscalations.escalationRules);

export const useRuleDetailsList = (
  ruleType: keyof typeof POINTS_RULE_TYPES,
  ruleSubType: keyof typeof POINTS_RULE_SUBTYPES
) => useSelector((state: RootState) => state.pointsSettings.rules[ruleType][ruleSubType]);

export const usePolicyDetails = () => useSelector((state: RootState) => state.pointsSettings);

export default pointsSettingsSlice.reducer;
